import React from "react";
import {
  Badge,
  Button,
  Card,
  CardBody,
  CardHeader,
  FormControl,
  FormLabel,
  Grid,
  Heading,
  Input,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  Select,
  SimpleGrid,
  Stack,
  Table,
  TableContainer,
  Tbody,
  Td,
  Text,
  Textarea,
  Th,
  Thead,
  Tr,
} from "@chakra-ui/react";
import { CARD_HELP_TEXT, CardHeadingWithHelp } from "../shared/help";

const ComplianceSection = ({
  canManagePayroll,
  canFinance,
  statutoryForm,
  setStatutoryForm,
  handleSaveStatutory,
  complianceDashboard,
  meta,
  formatPayrollCurrency,
  complianceEdits,
  setComplianceEdits,
  complianceModalRecord,
  setComplianceModalRecord,
  isComplianceModalOpen,
  openComplianceModal,
  closeComplianceModal,
  handleComplianceUpdate,
}) => {
  return (
    <Stack spacing={6}>
      {canManagePayroll || canFinance ? (
        <Card>
          <CardHeader>
            <CardHeadingWithHelp
              title="Statutory Configuration"
              helpText={CARD_HELP_TEXT.statutoryConfiguration}
            />
          </CardHeader>
          <CardBody>
            <Grid
              templateColumns={{ base: "1fr", md: "repeat(2, 1fr)" }}
              gap={4}
            >
              <FormControl>
                <FormLabel>PF Employee %</FormLabel>
                <Input
                  value={statutoryForm.pfEmployeeContribution}
                  onChange={(e) =>
                    setStatutoryForm((prev) => ({
                      ...prev,
                      pfEmployeeContribution: e.target.value,
                    }))
                  }
                />
              </FormControl>
              <FormControl>
                <FormLabel>PF Employer %</FormLabel>
                <Input
                  value={statutoryForm.pfEmployerContribution}
                  onChange={(e) =>
                    setStatutoryForm((prev) => ({
                      ...prev,
                      pfEmployerContribution: e.target.value,
                    }))
                  }
                />
              </FormControl>
              <FormControl>
                <FormLabel>PF Wage Limit (Rupees)</FormLabel>
                <Input
                  value={statutoryForm.pfWageLimit}
                  onChange={(e) =>
                    setStatutoryForm((prev) => ({
                      ...prev,
                      pfWageLimit: e.target.value,
                    }))
                  }
                />
              </FormControl>
              <FormControl>
                <FormLabel>ESI Wage Limit (Rupees)</FormLabel>
                <Input
                  value={statutoryForm.esiWageLimit}
                  onChange={(e) =>
                    setStatutoryForm((prev) => ({
                      ...prev,
                      esiWageLimit: e.target.value,
                    }))
                  }
                />
              </FormControl>
              <FormControl>
                <FormLabel>ESI Employee %</FormLabel>
                <Input
                  value={statutoryForm.esiEmployeeContribution}
                  onChange={(e) =>
                    setStatutoryForm((prev) => ({
                      ...prev,
                      esiEmployeeContribution: e.target.value,
                    }))
                  }
                />
              </FormControl>
              <FormControl>
                <FormLabel>ESI Employer %</FormLabel>
                <Input
                  value={statutoryForm.esiEmployerContribution}
                  onChange={(e) =>
                    setStatutoryForm((prev) => ({
                      ...prev,
                      esiEmployerContribution: e.target.value,
                    }))
                  }
                />
              </FormControl>
              <FormControl>
                <FormLabel>PT State</FormLabel>
                <Input
                  value={statutoryForm.ptState}
                  onChange={(e) =>
                    setStatutoryForm((prev) => ({
                      ...prev,
                      ptState: e.target.value,
                    }))
                  }
                />
              </FormControl>
              <FormControl>
                <FormLabel>TDS Regime</FormLabel>
                <Select
                  value={statutoryForm.tdsRegime}
                  onChange={(e) =>
                    setStatutoryForm((prev) => ({
                      ...prev,
                      tdsRegime: e.target.value,
                    }))
                  }
                >
                  <option value="NEW">NEW</option>
                  <option value="OLD">OLD</option>
                </Select>
              </FormControl>
              <FormControl gridColumn={{ md: "1 / span 2" }}>
                <FormLabel>PT Slabs JSON</FormLabel>
                <Textarea
                  rows={8}
                  value={statutoryForm.ptSlabsJson}
                  onChange={(e) =>
                    setStatutoryForm((prev) => ({
                      ...prev,
                      ptSlabsJson: e.target.value,
                    }))
                  }
                />
              </FormControl>
            </Grid>
            <Button mt={4} colorScheme="blue" onClick={handleSaveStatutory}>
              Save Statutory Rules
            </Button>
          </CardBody>
        </Card>
      ) : null}

      <SimpleGrid columns={{ base: 1, md: 4 }} spacing={4}>
        <Card>
          <CardBody>
            <Text color="gray.500">Total Records</Text>
            <Heading size="md">
              {complianceDashboard?.summary?.total || 0}
            </Heading>
          </CardBody>
        </Card>
        <Card>
          <CardBody>
            <Text color="gray.500">Overdue</Text>
            <Heading size="md">
              {complianceDashboard?.summary?.overdue || 0}
            </Heading>
          </CardBody>
        </Card>
        <Card>
          <CardBody>
            <Text color="gray.500">Due Soon</Text>
            <Heading size="md">
              {complianceDashboard?.summary?.dueSoon || 0}
            </Heading>
          </CardBody>
        </Card>
        <Card>
          <CardBody>
            <Text color="gray.500">Outstanding</Text>
            <Heading size="md">
              {formatPayrollCurrency(
                complianceDashboard?.summary?.totalOutstanding || 0,
                meta,
              )}
            </Heading>
          </CardBody>
        </Card>
      </SimpleGrid>

      <Card>
        <CardHeader>
          <CardHeadingWithHelp
            title="Compliance Records"
            helpText={CARD_HELP_TEXT.complianceRecords}
          />
        </CardHeader>
        <CardBody>
          <TableContainer>
            <Table size="sm">
              <Thead>
                <Tr>
                  <Th>Type</Th>
                  <Th>Period</Th>
                  <Th>Amount</Th>
                  <Th>Due</Th>
                  <Th>Status</Th>
                  <Th>Challan Ref</Th>
                  {canFinance ? <Th>Actions</Th> : null}
                </Tr>
              </Thead>
              <Tbody>
                {(complianceDashboard?.records || []).map((record) => {
                  const draft = complianceEdits[record._id] || {};
                  return (
                    <Tr key={record._id}>
                      <Td>{record.type}</Td>
                      <Td>{`${record.month}/${record.year}`}</Td>
                      <Td>{formatPayrollCurrency(record.totalAmount, meta)}</Td>
                      <Td>{new Date(record.dueDate).toLocaleDateString()}</Td>
                      <Td>
                        <Badge
                          colorScheme={
                            (draft.status || record.status) === "PAID"
                              ? "green"
                              : (draft.status || record.status) === "FILED"
                                ? "blue"
                                : "yellow"
                          }
                        >
                          {draft.status || record.status}
                        </Badge>
                      </Td>
                      <Td>{draft.challanRef ?? record.challanRef ?? "-"}</Td>
                      {canFinance ? (
                        <Td>
                          <Button
                            size="xs"
                            variant="outline"
                            onClick={() => {
                              setComplianceModalRecord(record);
                              setComplianceEdits((prev) => ({
                                ...prev,
                                [record._id]: {
                                  status: record.status,
                                  challanRef: record.challanRef || "",
                                  paymentReference:
                                    record.paymentReference || "",
                                  notes: record.notes || "",
                                  filedAt: record.filedAt
                                    ? record.filedAt.slice(0, 10)
                                    : "",
                                  paidAt: record.paidAt
                                    ? record.paidAt.slice(0, 10)
                                    : "",
                                  ...prev[record._id],
                                },
                              }));
                              openComplianceModal();
                            }}
                          >
                            Edit Details
                          </Button>
                        </Td>
                      ) : null}
                    </Tr>
                  );
                })}
              </Tbody>
            </Table>
          </TableContainer>
        </CardBody>
      </Card>

      <Modal
        isOpen={isComplianceModalOpen}
        onClose={closeComplianceModal}
        size="lg"
      >
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>
            Edit Compliance Record
            {complianceModalRecord
              ? ` — ${complianceModalRecord.type} ${complianceModalRecord.month}/${complianceModalRecord.year}`
              : ""}
          </ModalHeader>
          <ModalCloseButton />
          <ModalBody>
            {complianceModalRecord &&
              (() => {
                const id = complianceModalRecord._id;
                const draft = complianceEdits[id] || {};
                const setDraft = (field, value) =>
                  setComplianceEdits((prev) => ({
                    ...prev,
                    [id]: { ...prev[id], [field]: value },
                  }));
                return (
                  <Stack spacing={4}>
                    <FormControl>
                      <FormLabel>Status</FormLabel>
                      <Select
                        value={draft.status || "PENDING"}
                        onChange={(e) => setDraft("status", e.target.value)}
                      >
                        <option value="PENDING">PENDING</option>
                        <option value="FILED">FILED</option>
                        <option value="PAID">PAID</option>
                      </Select>
                    </FormControl>
                    <FormControl>
                      <FormLabel>Challan Reference</FormLabel>
                      <Input
                        value={draft.challanRef || ""}
                        placeholder="e.g. CHALLAN-2025-001"
                        onChange={(e) => setDraft("challanRef", e.target.value)}
                      />
                    </FormControl>
                    <FormControl>
                      <FormLabel>Payment Reference</FormLabel>
                      <Input
                        value={draft.paymentReference || ""}
                        placeholder="e.g. UTR number"
                        onChange={(e) =>
                          setDraft("paymentReference", e.target.value)
                        }
                      />
                    </FormControl>
                    <SimpleGrid columns={2} spacing={4}>
                      <FormControl>
                        <FormLabel>Filed At</FormLabel>
                        <Input
                          type="date"
                          value={draft.filedAt || ""}
                          onChange={(e) => setDraft("filedAt", e.target.value)}
                        />
                      </FormControl>
                      <FormControl>
                        <FormLabel>Paid At</FormLabel>
                        <Input
                          type="date"
                          value={draft.paidAt || ""}
                          onChange={(e) => setDraft("paidAt", e.target.value)}
                        />
                      </FormControl>
                    </SimpleGrid>
                    <FormControl>
                      <FormLabel>Notes</FormLabel>
                      <Textarea
                        rows={3}
                        value={draft.notes || ""}
                        placeholder="Filing notes, portal reference, officer name..."
                        onChange={(e) => setDraft("notes", e.target.value)}
                      />
                    </FormControl>
                  </Stack>
                );
              })()}
          </ModalBody>
          <ModalFooter gap={2}>
            <Button variant="ghost" onClick={closeComplianceModal}>
              Cancel
            </Button>
            <Button
              colorScheme="blue"
              onClick={async () => {
                await handleComplianceUpdate(complianceModalRecord._id);
                closeComplianceModal();
              }}
            >
              Save Changes
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>
    </Stack>
  );
};

export default ComplianceSection;
